import { openDB, DBSchema, IDBPDatabase } from 'idb';

export interface RadarSession {
  id: string;
  name: string;
  startTime: number;
  endTime: number | null;
  packetCount: number;
  active: boolean;
}

export interface StoredPacket {
  id?: number;
  sessionId: string;
  timestamp: number;
  lat: number | null;
  lon: number | null;
  accuracy: number | null;
  hasGeo: boolean;
  from: number;
  fromHex: string;
  fromName: string;
  to: number;
  toHex: string;
  relayNode: number;
  relayName: string;
  snr: number;
  rssi: number;
  hopLimit: number;
  hopStart: number;
  hopsTaken: number;
  portnum: number;
  portName: string;
  payloadSummary: string;
  isAck: boolean;
  packetId: number;
}

export interface StoredNode {
  num: number;
  hexId: string;
  shortName: string;
  longName: string;
  hwModel: number;
  macaddr?: string;
  lastHeard: number;
  lastSnr?: number;
  lastRssi?: number;
  lat?: number | null;
  lon?: number | null;
  byteHash: number; // (num & 0xFF) for 8-bit relay matching
}

export interface StoredTrace {
  packetId: number;
  sessionId: string;
  sentAt: number;
  targetNode: number;
  targetHex: string;
  lat: number | null;
  lon: number | null;
  ackReceived: boolean;
  ackAt: number | null;
  rttMs: number | null;
  pathNodes: number[];
  pathSnrs: number[];
}

export interface NodeRadarDBSchema extends DBSchema {
  sessions: {
    key: string;
    value: RadarSession;
    indexes: { 'by-startTime': number };
  };
  packets: {
    key: number;
    value: StoredPacket;
    indexes: {
      'by-session': string;
      'by-timestamp': number;
      'by-from': number;
      'by-relay': number;
      'by-snr': number;
    };
  };
  nodes: {
    key: number;
    value: StoredNode;
    indexes: {
      'by-byteHash': number;
      'by-shortName': string;
      'by-lastHeard': number;
    };
  };
  traces: {
    key: number;
    value: StoredTrace;
    indexes: {
      'by-session': string;
      'by-target': number;
    };
  };
}

const DB_NAME = 'NodeRadarDB';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<NodeRadarDBSchema>> | null = null;

/**
 * Initializes and retrieves the singleton IndexedDB database connection.
 * Creates the required object stores and indexes on first launch.
 *
 * @returns Promise resolving to the IDBPDatabase instance
 */
export async function getDb(): Promise<IDBPDatabase<NodeRadarDBSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<NodeRadarDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Sessions store
        const sessionStore = db.createObjectStore('sessions', { keyPath: 'id' });
        sessionStore.createIndex('by-startTime', 'startTime');

        // Packets store
        const packetStore = db.createObjectStore('packets', { keyPath: 'id', autoIncrement: true });
        packetStore.createIndex('by-session', 'sessionId');
        packetStore.createIndex('by-timestamp', 'timestamp');
        packetStore.createIndex('by-from', 'from');
        packetStore.createIndex('by-relay', 'relayNode');
        packetStore.createIndex('by-snr', 'snr');

        // Nodes store
        const nodeStore = db.createObjectStore('nodes', { keyPath: 'num' });
        nodeStore.createIndex('by-byteHash', 'byteHash');
        nodeStore.createIndex('by-shortName', 'shortName');
        nodeStore.createIndex('by-lastHeard', 'lastHeard');

        // Traces store
        const traceStore = db.createObjectStore('traces', { keyPath: 'packetId' });
        traceStore.createIndex('by-session', 'sessionId');
        traceStore.createIndex('by-target', 'targetNode');
      }
    });
  }
  return dbPromise;
}
